import cn from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { usePopover } from '@app/components/Popover/hooks/usePopover';
import type { PopoverPropsInterface as Props } from '@app/components/Popover/types';
import { emptyFn } from '@app/constants';

import type { CSSProperties, FC } from 'react';

import '@app/components/Popover/styles/Popover.desktop.scss';

type FloatingPosition = {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
};

export const Popover: FC<Props> = ({
  disabled = false,
  component,
  content,
  componentClassName = '',
  wrapperClassName = '',
  contentClassName = '',
  componentStyle,
  contentStyle,
  wrapperStyle,
  self = false,

  strategy = 'absolute',
  offsetPx = 8,
}) => {
  const { isOpen, toggleHandler, wrapperRef, contentRef, open, close } =
    usePopover({ disabled });

  const buttonRef = useRef<HTMLButtonElement>(null);

  const [floating, setFloating] = useState<FloatingPosition | null>(null);

  const updateFloatingPosition = useCallback(() => {
    if (strategy !== 'fixed') return;
    const buttonEl = buttonRef.current;
    const contentEl = contentRef.current;

    if (!buttonEl || !contentEl) return;

    const rect = buttonEl.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    const isOffscreen =
      rect.bottom < 0 ||
      rect.top > viewportH ||
      rect.right < 0 ||
      rect.left > viewportW;

    if (isOffscreen) {
      close();
      return;
    }

    const margin = 8;

    let { width } = rect;
    let { left } = rect;

    const maxAllowedWidth = Math.max(0, viewportW - margin * 2);
    if (width > maxAllowedWidth) width = maxAllowedWidth;

    if (left + width > viewportW - margin) left = viewportW - margin - width;
    if (left < margin) left = margin;

    let top = rect.bottom + offsetPx;
    let maxHeight = Math.max(0, viewportH - top - margin);

    const contentHeight = contentEl.getBoundingClientRect().height;
    const minUsefulSpace = 120;

    if (maxHeight < minUsefulSpace) {
      const spaceAbove = rect.top - offsetPx - margin;
      if (spaceAbove > minUsefulSpace) {
        top = Math.max(margin, rect.top - offsetPx - contentHeight);
        maxHeight = spaceAbove;
      }
    }

    setFloating({ top, left, width, maxHeight });
  }, [strategy, offsetPx, close, contentRef]);

  useEffect(() => {
    if (!isOpen || strategy !== 'fixed') return;

    let rafId = 0;

    const schedule = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateFloatingPosition);
    };

    schedule();
    requestAnimationFrame(schedule);

    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('scroll', schedule, {
      passive: true,
      capture: true,
    });

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('scroll', schedule, true);
    };
  }, [isOpen, strategy, updateFloatingPosition]);

  const baseVisibilityStyle = useMemo(
    () => ({
      opacity: isOpen ? 1 : 0,
      visibility: (isOpen
        ? 'visible'
        : 'hidden') as CSSProperties['visibility'],
      translate: isOpen ? '0' : '0 -20px',
      pointerEvents: (isOpen
        ? 'auto'
        : 'none') as CSSProperties['pointerEvents'],
    }),
    [isOpen]
  );

  const computedFixedStyle = useMemo(() => {
    if (strategy !== 'fixed' || !floating) return null;

    return {
      position: 'fixed' as const,
      top: floating.top,
      left: floating.left,
      width: floating.width,
      maxHeight: floating.maxHeight,
      overflow: 'auto',
      zIndex: 1000,
    };
  }, [strategy, floating]);

  const contentNode = (
    <div
      ref={contentRef}
      className={cn('ui-popover-content', contentClassName)}
      style={{
        ...baseVisibilityStyle,
        ...(computedFixedStyle ?? {}),
        ...contentStyle,
      }}
    >
      {content(isOpen, open, close)}
    </div>
  );

  return (
    <div
      ref={wrapperRef}
      className={cn('ui-popover-wrapper', wrapperClassName)}
      style={wrapperStyle}
    >
      <button
        ref={buttonRef}
        onClick={!disabled && self ? toggleHandler : emptyFn}
        className={cn('ui-popover-button-component', componentClassName, {
          'ui-popover-button-component-disabled': disabled,
        })}
        style={componentStyle}
      >
        {component(isOpen, open, close)}
      </button>

      {strategy === 'fixed' && typeof document !== 'undefined'
        ? createPortal(contentNode, document.body)
        : contentNode}
    </div>
  );
};
